"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useJobWizardStore } from "@/features/jobs/store/job-store";
import { useAuth } from "@/features/auth";
import { useProjectSwitch } from "@/features/projects/hooks/use-project-switch";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

export type Step3UploadStatus = "idle" | "uploading" | "failed";

export function WizardFooter({
  onNext,
  isBusy,
  isNextDisabled,
  step3UploadStatus = "idle",
  step3FailedCount = 0,
}: {
  onNext: () => void;
  isBusy: boolean;
  isNextDisabled: boolean;
  step3UploadStatus?: Step3UploadStatus;
  step3FailedCount?: number;
}) {
  const {
    step,
    prevStep,
    closeWizard,
    createdJobId,
    createdJobProjectId,
    form,
    updateForm,
    mode,
  } = useJobWizardStore();
  const isCloneMode = mode === "clone";
  const router = useRouter();
  const { selectedProjectId } = useAuth();
  const { switchProjectAsync } = useProjectSwitch();
  const [isFinishing, setIsFinishing] = useState(false);

  const handleDone = useCallback(async () => {
    if (!createdJobId || isFinishing) return;
    setIsFinishing(true);
    try {
      if (
        createdJobProjectId &&
        createdJobProjectId !== selectedProjectId
      ) {
        await switchProjectAsync(createdJobProjectId);
      }
      closeWizard();
      router.push(`/jobs/${createdJobId}`);
    } catch (err) {
      showToast.error("Couldn't switch project", {
        description: getApiErrorMessage(err),
      });
    } finally {
      setIsFinishing(false);
    }
  }, [
    closeWizard,
    createdJobId,
    createdJobProjectId,
    isFinishing,
    router,
    selectedProjectId,
    switchProjectAsync,
  ]);

  const handleRemoveAllFailed = useCallback(() => {
    const remaining = form.uploadedFiles.filter((f) => f.state !== "failed");
    updateForm("uploadedFiles", remaining);
  }, [form.uploadedFiles, updateForm]);

  if (step >= 4) {
    return (
      <>
        <div />
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void handleDone()}
          disabled={isFinishing}
        >
          Done
        </Button>
      </>
    );
  }

  const importPath = form.sourcingType === "ats_import";
  const previousButton =
    step > 1 && !(importPath && step === 2) ? (
      <Button type="button" variant="secondary" size="sm" onClick={prevStep}>
        Previous Step
      </Button>
    ) : (
      <div />
    );

  if (step === 3) {
    if (step3UploadStatus === "uploading") {
      return (
        <>
          {previousButton}
          <SubmitButton
            type="button"
            variant="secondary"
            size="sm"
            disabled
            loading
            loadingLabel="Uploading"
          >
            Uploading
          </SubmitButton>
        </>
      );
    }

    if (step3UploadStatus === "failed") {
      return (
        <>
          {previousButton}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative inline-flex h-7 cursor-pointer items-center justify-center gap-1.5 overflow-clip rounded-sm border border-destructive/30 bg-destructive/10 px-2.5 text-sm font-medium text-destructive shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:bg-destructive/15"
              >
                <span>
                  {step3FailedCount} Issue{step3FailedCount === 1 ? "" : "s"}{" "}
                  Found
                </span>
                <Icon
                  name="IconWarningSign"
                  size={14}
                  className="text-destructive"
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={6}
              className="w-[280px] p-2"
            >
              <div className="flex flex-col gap-2">
                <div className="px-2 pt-1 pb-2 border-b border-contrast/8">
                  <p className="text-sm font-medium text-heading">
                    {step3FailedCount} file{step3FailedCount === 1 ? "" : "s"}{" "}
                    failed to upload
                  </p>
                  <p className="text-xs text-contrast/60 mt-0.5">
                    Hover a failed row to see the reason, or remove them all at
                    once.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveAllFailed}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-destructive hover:bg-destructive/10 cursor-pointer"
                >
                  <Icon name="IconTrashCan" size={14} />
                  <span>Remove all failed</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </>
      );
    }

    return (
      <>
        {previousButton}
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          onClick={onNext}
          disabled={isNextDisabled}
          loading={isBusy}
          loadingLabel={isCloneMode ? "Cloning" : "Creating"}
          icon={<Icon name="IconArrowRight" size={14} />}
        >
          Create Job
        </SubmitButton>
      </>
    );
  }

  return (
    <>
      {previousButton}
      <SubmitButton
        type="button"
        variant="primary"
        size="sm"
        onClick={onNext}
        disabled={isNextDisabled}
        loading={isBusy}
        loadingLabel={
          step === 1 && form.sourcingType === "ats_import"
            ? "Importing"
            : isCloneMode && step === 1
              ? "Cloning"
              : "Continuing"
        }
      >
        {step === 1 && form.sourcingType === "ats_import"
          ? "Import from ATS"
          : "Continue"}
      </SubmitButton>
    </>
  );
}
